// ============================================
// Core Game Types
// ============================================

/** 2D vector for tilt input, UI positions, etc. */
export interface Vec2 {
  x: number;
  y: number;
}

/** 3D vector for positions, forces */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

// ============================================
// Vector Conversion Utilities
// ============================================

import { Vector3 } from '@babylonjs/core';

/** Convert plain Vec3 to Babylon Vector3 */
export const toVector3 = (v: Vec3): Vector3 => new Vector3(v.x, v.y, v.z);

// ============================================
// Input State
// ============================================

export interface InputState {
  /** Tilt vector, normalized to [-1, 1] range */
  tilt: Vec2;
  /** Raw device orientation (for debugging) */
  rawTilt: Vec2;
  /** Left flipper pressed */
  leftFlipper: boolean;
  /** Right flipper pressed */
  rightFlipper: boolean;
  /** Tilt permission granted */
  motionPermission: 'unknown' | 'granted' | 'denied' | 'unavailable';
}

// ============================================
// Game Mode / Run State
// ============================================

export type GameMode =
  | 'loading'
  | 'menu'
  | 'permission-gate' // Waiting for motion permission
  | 'playing'
  | 'paused'
  | 'fell'     // Ball fell in hole, tap to restart
  | 'finished';

export interface RunState {
  mode: GameMode;
  /** Current run elapsed time in seconds */
  elapsedTime: number;
  /** Targets hit this run (by ID) */
  targetsHit: Set<string>;
  /** Total targets required */
  totalTargets: number;
  /** Is finish gate open? */
  gateOpen: boolean;
  /** Best time for current table (null if none) */
  bestTime: number | null;
  /** Medal earned this run */
  medal: 'none' | 'bronze' | 'silver' | 'gold';
}

export const DEFAULT_RUN_STATE: RunState = {
  mode: 'loading',
  elapsedTime: 0,
  targetsHit: new Set(),
  totalTargets: 0,
  gateOpen: false,
  bestTime: null,
  medal: 'none',
};

// ============================================
// Table Definition
// ============================================

export interface TargetDef {
  id: string;
  position: Vec3;
}

export interface HoleDef {
  id: string;
  position: Vec3;
  radius: number;
}

export interface WallSegment {
  /** Start point (x, z) */
  start: Vec2;
  /** End point (x, z) */
  end: Vec2;
  /** Wall height */
  height: number;
}

export interface FlipperDef {
  id: string;
  position: Vec3;
  /** 'left' or 'right' determines rotation direction */
  side: 'left' | 'right';
  length: number;
}

export interface ITableDef {
  id: string;
  name: string;
  /** Board dimensions */
  width: number;
  length: number;
  /** Ball spawn position */
  spawnPosition: Vec3;
  /** Finish gate position */
  finishPosition: Vec3;
  /** Target definitions */
  targets: TargetDef[];
  /** Hole/hazard definitions */
  holes: HoleDef[];
  /** Wall segments */
  walls: WallSegment[];
  /** Flipper definitions */
  flippers: FlipperDef[];
  /** Medal time thresholds in seconds */
  medalTimes: {
    gold: number;
    silver: number;
    bronze: number;
  };
  /** Theme/style overrides */
  theme?: {
    boardColor?: string;
    wallColor?: string;
    accentColor?: string;
  };
}

// ============================================
// Persist / Settings
// ============================================

export interface GameSettings {
  /** Invert tilt X axis */
  invertTiltX: boolean;
  /** Invert tilt Y axis */
  invertTiltY: boolean;
  /** Tilt sensitivity multiplier (0.5 - 2.0) */
  tiltSensitivity: number;
  /** Show debug overlay */
  showDebug: boolean;
  /** Sound effects volume (0 - 1) */
  sfxVolume: number;
  /** Music volume (0 - 1) */
  musicVolume: number;
}

export const DEFAULT_SETTINGS: GameSettings = {
  invertTiltX: false,
  invertTiltY: false,
  tiltSensitivity: 1.0,
  showDebug: false,
  sfxVolume: 1.0,
  musicVolume: 0.7,
};

export interface SaveData {
  settings: GameSettings;
  /** Best times by table ID */
  bestTimes: Record<string, number>;
  /** Unlocked tables */
  unlockedTables: string[];
  /** Calibration offset */
  calibrationOffset: Vec2;
  /** Motion permission was granted */
  motionPermissionGranted: boolean;
}
